import React, { useEffect, useMemo, useState } from 'react';
import { keyframes } from 'tss-react';
import { makeStyles } from 'tss-react/mui';
import i18next from '../../../base/i18n/i18next';
import { getLogoWhiteUrl, getWhitelabelConfig } from '../../../base/whitelabel/functions';
import { describeCallStage, formatCallStage, formatPeerTitle, isJoinedStage, resolveRoomWaitingMedia, resolveWaitingSplashPalette, withAlpha } from '../../peerWaiting';
import { useCallStage } from '../../useCallStage';
import { usePeerDisplayName } from '../../usePeerDisplayName';
/**
 * The peer's pane until their first video frame: Robert's waiting splash
 * (the tenant's brand animation, or its configured silent loop / still /
 * signed welcome) behind the peer's name and an honest stage line.
 *
 * It fills the pane the peer's video will fill, so the self-view never moves:
 * VRSLayout lays the room out as if the peer were already there. When the
 * first remote frame plays (`live`) the splash fades out over the real video,
 * which VRSLayout fades in underneath, and then unmounts for good for this
 * participant.
 *
 * The splash is decoration and hidden from assistive technology; the name and
 * the stage line are the content, and the stage line is a polite live region.
 */
/** The cross-fade, matched by VRSLayout's video transition. */
export const PEER_PANE_FADE_MS = 450;
const REDUCED_MOTION_QUERY = '(prefers-reduced-motion: reduce)';
function tr(key, options) {
    return String(i18next.t(key, options ?? {}));
}
const ripple = keyframes `
    0% {
        opacity: 0;
        transform: scale(0.62);
    }
    18% {
        opacity: 0.6;
    }
    100% {
        opacity: 0;
        transform: scale(1.35);
    }
`;
const breathe = keyframes `
    0%, 100% {
        opacity: 0.8;
        transform: scale(0.98);
    }
    50% {
        opacity: 1;
        transform: scale(1);
    }
`;
const useStyles = makeStyles()(() => ({
    root: {
        alignItems: 'center',
        borderRadius: 'inherit',
        color: '#F5F8FB',
        display: 'flex',
        flexDirection: 'column',
        gap: 18,
        inset: 0,
        justifyContent: 'center',
        opacity: 1,
        overflow: 'hidden',
        position: 'absolute',
        transition: `opacity ${PEER_PANE_FADE_MS}ms ease`,
        zIndex: 2,
        '@media (prefers-reduced-motion: reduce)': {
            transition: 'none'
        }
    },
    fading: {
        opacity: 0,
        pointerEvents: 'none'
    },
    backdrop: {
        inset: 0,
        pointerEvents: 'none',
        position: 'absolute'
    },
    media: {
        height: '100%',
        objectFit: 'cover',
        width: '100%'
    },
    // Signing is content: shown whole, like every remote signer.
    welcome: {
        background: '#000',
        height: '100%',
        inset: 0,
        objectFit: 'contain',
        position: 'absolute',
        width: '100%'
    },
    mark: {
        alignItems: 'center',
        aspectRatio: '1 / 1',
        display: 'flex',
        flexShrink: 0,
        justifyContent: 'center',
        position: 'relative',
        width: 'clamp(88px, 24%, 180px)'
    },
    ring: {
        animation: `${ripple} 3.2s cubic-bezier(0.2, 0.6, 0.35, 1) infinite`,
        borderRadius: '50%',
        borderStyle: 'solid',
        borderWidth: 2,
        inset: 0,
        opacity: 0,
        position: 'absolute',
        '@media (prefers-reduced-motion: reduce)': {
            animation: 'none',
            opacity: 0.45,
            transform: 'scale(0.92)'
        }
    },
    ringLate: {
        animationDelay: '1.6s',
        '@media (prefers-reduced-motion: reduce)': {
            display: 'none'
        }
    },
    logo: {
        animation: `${breathe} 3.2s ease-in-out infinite`,
        maxHeight: '46%',
        objectFit: 'contain',
        position: 'relative',
        width: '78%',
        '@media (prefers-reduced-motion: reduce)': {
            animation: 'none'
        }
    },
    caption: {
        maxWidth: 'min(440px, 90%)',
        padding: '0 16px',
        position: 'relative',
        textAlign: 'center'
    },
    captionOverMedia: {
        background: 'linear-gradient(to top, rgba(4, 4, 4, 0.84), rgba(4, 4, 4, 0))',
        bottom: 0,
        left: 0,
        maxWidth: 'none',
        padding: '48px 20px 20px',
        position: 'absolute',
        right: 0
    },
    name: {
        fontSize: 'clamp(18px, 2.2vw, 26px)',
        fontWeight: 700,
        lineHeight: 1.25,
        margin: 0,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        textShadow: '0 1px 2px rgba(0, 0, 0, 0.55)',
        whiteSpace: 'nowrap'
    },
    stage: {
        color: 'rgba(235, 242, 250, 0.92)',
        fontSize: 15,
        lineHeight: 1.45,
        margin: '6px 0 0',
        minHeight: '1.45em',
        textShadow: '0 1px 2px rgba(0, 0, 0, 0.55)'
    },
    stageProblem: {
        color: '#FFD9D4',
        fontWeight: 600
    },
    welcomeButton: {
        background: 'rgba(255, 255, 255, 0.12)',
        border: '1px solid rgba(255, 255, 255, 0.45)',
        borderRadius: 999,
        color: '#FFFFFF',
        cursor: 'pointer',
        font: 'inherit',
        fontWeight: 600,
        marginTop: 12,
        minHeight: 44,
        padding: '8px 16px',
        '&:focus-visible': {
            outline: '2px solid #FFFFFF',
            outlineOffset: 2
        }
    }
}));
function reducedMotionQuery() {
    return typeof window !== 'undefined' && typeof window.matchMedia === 'function'
        ? window.matchMedia(REDUCED_MOTION_QUERY)
        : undefined;
}
function usePrefersReducedMotion() {
    const [reduced, setReduced] = useState(() => Boolean(reducedMotionQuery()?.matches));
    useEffect(() => {
        const query = reducedMotionQuery();
        if (!query) {
            return undefined;
        }
        const onChange = (event) => setReduced(event.matches);
        setReduced(query.matches);
        if (typeof query.addEventListener === 'function') {
            query.addEventListener('change', onChange);
            return () => query.removeEventListener('change', onChange);
        }
        query.addListener(onChange);
        return () => query.removeListener(onChange);
    }, []);
    return reduced;
}
/**
 * Autoplay needs the muted PROPERTY set before playback starts; React's
 * `muted` prop alone has not always reached the element in time. The splash
 * never plays sound.
 */
function keepMuted(element) {
    if (element) {
        element.defaultMuted = true;
        element.muted = true;
    }
}
const PeerWaitingPane = ({ peer, peerId, peerName, peerPresent, video }) => {
    const { classes, cx } = useStyles();
    const reducedMotion = usePrefersReducedMotion();
    const live = peerPresent && video === 'started';
    const { slow, stage } = useCallStage({ present: peerPresent, video: peerPresent ? video : 'none' });
    const name = usePeerDisplayName(peer.name, peerPresent, peerName);
    const identity = peerId || '';
    // A pane that first mounts over video already playing never shows the
    // splash at all (no fade over a live signer).
    const [liveFor, setLiveFor] = useState(() => (live ? identity : undefined));
    const [welcome, setWelcome] = useState('pending');
    const [loopFailed, setLoopFailed] = useState(false);
    const splash = useMemo(() => {
        const config = getWhitelabelConfig();
        return {
            logo: getLogoWhiteUrl(),
            media: resolveRoomWaitingMedia(config, 'web'),
            palette: resolveWaitingSplashPalette(config?.theme)
        };
    }, []);
    // First frame: fade out over the real video, then leave for good for
    // this participant (the pane stays mounted, so nothing restarts when
    // they arrive; if they leave, the pane waits again).
    useEffect(() => {
        if (!live || liveFor === identity) {
            return undefined;
        }
        if (reducedMotion) {
            setLiveFor(identity);
            return undefined;
        }
        const timer = setTimeout(() => setLiveFor(identity), PEER_PANE_FADE_MS);
        return () => clearTimeout(timer);
    }, [identity, live, liveFor, reducedMotion]);
    if (liveFor !== undefined && liveFor === identity) {
        return null;
    }
    const { logo, media, palette } = splash;
    // Videos load only once the conference is joined, so they never compete
    // with the join itself for the connection.
    const joined = isJoinedStage(stage);
    const welcomeAvailable = Boolean(media.signedWelcomeVideo) && joined && !live && welcome !== 'done';
    const welcomePlaying = welcomeAvailable && (welcome === 'playing' || !reducedMotion);
    const loopVideo = !welcomePlaying && !loopFailed && !reducedMotion && joined && !live ? media.video : undefined;
    const still = !welcomePlaying && !loopVideo ? media.poster : undefined;
    const fullBleed = Boolean(welcomePlaying || loopVideo || still);
    const stageText = describeCallStage(stage, { kind: peer.kind, name, slow });
    return (React.createElement("div", { "aria-hidden": live || undefined, className: cx(classes.root, live && classes.fading), "data-peer-waiting-stage": stage, style: {
            background: `radial-gradient(120% 90% at 50% 38%, ${withAlpha(palette.glow, 0.55)} 0%, `
                + `${withAlpha(palette.glow, 0.16)} 42%, ${palette.base} 78%)`,
            backgroundColor: palette.base
        } },
        (loopVideo || still) && (React.createElement("div", { "aria-hidden": 'true', className: classes.backdrop }, loopVideo ? (React.createElement("video", { autoPlay: true, className: classes.media, disablePictureInPicture: true, loop: true, muted: true, onError: () => setLoopFailed(true), playsInline: true, poster: media.poster, preload: 'auto', ref: keepMuted, src: loopVideo })) : (React.createElement("img", { alt: '', className: classes.media, decoding: 'async', src: still })))),
        welcomePlaying && (React.createElement("video", { "aria-label": tr('vrs.layout.peerWaiting.signedWelcome'), autoPlay: true, className: classes.welcome, disablePictureInPicture: true, muted: true, onEnded: () => setWelcome('done'), onError: () => setWelcome('done'), playsInline: true, preload: 'auto', ref: keepMuted, src: media.signedWelcomeVideo })),
        !fullBleed && (React.createElement("div", { "aria-hidden": 'true', className: classes.mark },
            React.createElement("span", { className: classes.ring, style: { borderColor: withAlpha(palette.ring, 0.6) } }),
            React.createElement("span", { className: cx(classes.ring, classes.ringLate), style: { borderColor: withAlpha(palette.ring, 0.6) } }),
            React.createElement("img", { alt: '', className: classes.logo, decoding: 'async', src: logo }))),
        React.createElement("div", { className: cx(classes.caption, fullBleed && classes.captionOverMedia) },
            React.createElement("p", { className: classes.name }, formatPeerTitle({ kind: peer.kind, name }, tr)),
            React.createElement("p", { "aria-live": 'polite', className: cx(classes.stage, stageText?.tone === 'problem' && classes.stageProblem), role: 'status' }, stageText ? formatCallStage(stageText, tr) : ''),
            welcomeAvailable && !welcomePlaying && (React.createElement("button", { className: classes.welcomeButton, onClick: () => setWelcome('playing'), type: 'button' }, tr('vrs.layout.peerWaiting.playSignedWelcome'))))));
};
export default PeerWaitingPane;
